import { ApolloError, ApolloQueryResult } from "@apollo/client";
import {
  useDeleteImmigrationDocumentMutation,
  useExhibitFilesQuery,
  useImmigrationDocsByUserQuery,
  useUnlinkExhibitFileMutation,
} from "@codegen/index";
import { ImmigrationDocumentType } from "@codegen/schema";
import { FormContainer, OnboardingContainer } from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { UploadFileStatus } from "@components/uploader/types";
import Uploader from "@components/uploader/uploader";
import { OnboardingPageType } from "@pages/onboarding/types";
import * as Sentry from "@sentry/react";
import { api } from "@utils/api";
import { useLogError } from "@utils/error";
import { useJwt } from "@utils/hooks";
import React, { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { z } from "zod";
import { FormLoading } from "./loading";

export const eadCardFormSchema = z.object({
  eadCardStamps: z.string().min(1, "Please upload your EAD card"),
});

export type EADCardFormType = z.infer<typeof eadCardFormSchema>;

type EadCardFormViewProps = OnboardingPageType & {
  serverFiles: { id: string; name: string }[];
  refetch: () => Promise<
    ApolloQueryResult<ReturnType<typeof useImmigrationDocsByUserQuery>["data"]>
  >;
  queryLoading: boolean;
};

const EadCardFormView: React.FC<EadCardFormViewProps> = (props) => {
  const {
    contact,
    onBack,
    onSubmit,
    autoSave,
    onSkip,
    exhibit,
    onSkipForNow,
    moduleProgress,
    currentStep,
    showSkipForNow,
    hideExitButton,
    serverFiles,
    refetch,
    queryLoading,
  } = props;

  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const pendingSaveRef = useRef<Promise<void> | null>(null);
  const { isReadOnly } = useReadOnlyMode();
  const token = useJwt();
  const logError = useLogError();
  const fileType = ImmigrationDocumentType.EadCard;

  const [currentFiles, setCurrentFiles] = useState(serverFiles);

  const [unlinkExhibitFile] = useUnlinkExhibitFileMutation();
  const [deleteImmigrationDocument] = useDeleteImmigrationDocumentMutation();

  const { data: exhibitFiles } = useExhibitFilesQuery({
    variables: {
      id: exhibit.exhibitId?.toString() ?? "",
    },
  });

  const uploadFile = api.files.uploadImmigrationDoc;

  const handleSubmit = useCallback(async () => {
    try {
      if (pendingSaveRef.current) {
        await pendingSaveRef.current;
      }
      setStatus("loading");
      if (currentFiles?.length && currentFiles.length > 0) {
        await refetch();
        const fileIds = currentFiles?.map((n) => n?.id);
        autoSave({ files: fileIds });
        onSubmit({ files: fileIds });
      } else if (!exhibit.required) {
        onSkip?.();
      }
      setStatus("success");
    } catch (e) {
      setStatus("error");
      logError(e, { message: "Error submitting EAD card files" });
    }
  }, [
    currentFiles,
    refetch,
    exhibit.required,
    onSkip,
    autoSave,
    onSubmit,
    logError,
  ]);

  const handleSkipForNow = useCallback(async () => {
    try {
      if (currentFiles?.length && currentFiles.length > 0) {
        await refetch();
        const fileIds = currentFiles?.map((n) => n?.id);
        onSkipForNow?.({ files: fileIds });
      } else {
        onSkipForNow?.();
      }
    } catch (e) {
      logError(e, { message: "Error skipping" });
    }
  }, [currentFiles, refetch, onSkipForNow, logError]);

  const handleDelete = useCallback(
    async (file: UploadFileStatus) => {
      try {
        setStatus("loading");
        const exhibitFile =
          exhibitFiles?.exhibitById?.exhibitFilesByExhibitId?.nodes?.find(
            (f) => f?.fileByFileId?.id === file.id
          );

        if (exhibit.exhibitId && exhibitFile) {
          Sentry.addBreadcrumb({
            category: "exhibit",
            message: `Unlinking exhibit file ${file.id} from exhibit ${exhibit.exhibitId}`,
          });

          await unlinkExhibitFile({
            variables: {
              input: {
                clientMutationId: `unlink-exhibit-file-${exhibit.exhibitId}-${file.id}`,
                exhibitId: exhibit.exhibitId.toString(),
                fileId: file.id,
              },
            },
          });
        }
        setCurrentFiles((prev) => prev.filter((f) => f.id !== file.id));
        if (file.id) {
          try {
            const { errors } = await deleteImmigrationDocument({
              variables: {
                input: {
                  clientMutationId: `delete-user-immigration-doc-${fileType}-${contact.id}-${file.id}`,
                  ownerId: contact.id,
                  fileId: file.id,
                },
              },
            });

            if (errors != null) {
              throw new Error(errors[0].message);
            }
          } catch (e) {
            if (e instanceof Error) {
              logError(e, { contactId: contact.id, fileId: file.id });
            } else if (e instanceof ApolloError) {
              logError(e.graphQLErrors[0], {
                contactId: contact.id,
                fileId: file.id,
              });
            } else {
              logError(e, { contactId: contact.id, fileId: file.id });
            }
            throw e;
          }
        }

        await refetch();
        setStatus("success");
      } catch (e) {
        setStatus("error");
        logError(e, { contactId: contact.id, fileId: file.id });
        toast.error("An error occurred while deleting the file");
      }
    },
    [
      contact.id,
      exhibit.exhibitId,
      exhibitFiles,
      unlinkExhibitFile,
      deleteImmigrationDocument,
      fileType,
      refetch,
      logError,
    ]
  );

  const onFileUpload = useCallback(
    async (file: File) => {
      try {
        setStatus("loading");
        const uploadResult = await uploadFile(
          file,
          token,
          contact.id,
          fileType
        );

        if (!uploadResult.success || !uploadResult.id)
          throw new Error("An error occurred while uploading the file");

        const { id } = uploadResult;
        const newFile = {
          id,
          name: file.name,
          success: uploadResult.success,
          fileId: id,
        };

        // Update local state without triggering autoSave
        setCurrentFiles((prev) => [...prev, newFile]);
        await refetch();
        setStatus("success");
        return newFile;
      } catch (exception) {
        setStatus("error");
        logError(exception, {
          contactId: contact.id,
          fileType,
        });
        return {
          success: false,
          name: file.name,
          type: file.type,
          link: "",
          id: "",
        };
      }
    },
    [contact.id, token, fileType, refetch, logError]
  );

  return (
    <OnboardingContainer
      key={exhibit.id}
      title="Identification Documents"
      subtitle={
        "If you have ever worked in the U.S. using an 'EAD' (Employment Authorization Document) card, please upload these here. " +
        "If you have never received U.S. work authorization, this will not apply."
      }
      progress={moduleProgress}
      currentStep={currentStep}
      onSubmit={handleSubmit}
      exhibit={exhibit}
      name="ead-card-form"
      onBack={onBack}
      onSkipForNow={exhibit.required ? handleSkipForNow : onSkip}
      isLoading={status === "loading" || queryLoading}
      showSkipForNow={showSkipForNow}
      hideExitButton={hideExitButton}
    >
      <FormContainer
        status={status}
        errorMessage="There was a problem uploading your EAD card files. Please try again."
        successMessage={
          status === "success" ? "Files uploaded successfully." : undefined
        }
      >
        <div>
          <Uploader
            key={exhibit.id}
            multiple
            onFileUpload={onFileUpload}
            defaultFiles={currentFiles}
            disabled={isReadOnly}
            allowDelete
            onDeleteFile={handleDelete}
          />
        </div>
      </FormContainer>
    </OnboardingContainer>
  );
};

export const EadCardForm: React.FC<OnboardingPageType> = (props) => {
  const { data, loading, refetch } = useImmigrationDocsByUserQuery({
    variables: {
      ownerId: props.contact.id,
      type: ImmigrationDocumentType.EadCard,
    },
    fetchPolicy: "cache-and-network",
  });

  if (loading && !data?.allImmigrationDocuments) {
    return <FormLoading />;
  }

  const serverFiles =
    data?.allImmigrationDocuments?.nodes
      ?.map((node) => node?.fileByFileId)
      .filter((n) => !!n)
      .map((x) => ({
        id: x.id ?? "",
        name: x.name ?? "",
      })) ?? [];

  return (
    <EadCardFormView
      {...props}
      serverFiles={serverFiles}
      refetch={refetch}
      queryLoading={loading}
    />
  );
};

export default EadCardForm;
